// frontend/src/components/StatsDialog.js
import React, { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid, Paper, Box,
  IconButton, CircularProgress, AppBar, Toolbar, useMediaQuery // Hinzugefügt
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Hinzugefügt
import CloseIcon from '@mui/icons-material/Close';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'; // ResponsiveContainer hinzugefügt

import { useStats } from '../hooks/useStats';

const StatsDialog = ({ open, onClose }) => {
  const { statsData, isLoading, error, fetchAllStats } = useStats(open); // Lädt nur wenn open=true
  const theme = useTheme(); // Hinzugefügt
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Hinzugefügt

  const {
    topLocations = [], // Default zu leeren Arrays
    topGenres = [],
    totalBooks = 0,
    booksByLocation = [],
    booksWithoutCover = 0,
  } = statsData || {}; // Default zu leerem Objekt, falls statsData null ist

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="md">
      {/* Mobile Header */}
      {isMobile && (
          <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                      <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                      Statistiken
                  </Typography>
                  <Button color="inherit" onClick={onClose}>
                      Schließen
                  </Button>
              </Toolbar>
          </AppBar>
      )}
      {/* Desktop Title */}
      {!isMobile && (
          <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.main', color: 'white' }}>
              Statistiken
              <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
                  <CloseIcon />
              </IconButton>
          </DialogTitle>
      )}

      <DialogContent sx={isMobile ? { pt: 2 } : {}}>
        {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress />
            </Box>
        )}
        {error && <Typography color="error" sx={{ p: 2 }}>Fehler beim Laden der Statistiken: {error}</Typography>}

        {!isLoading && !error && statsData && (
          <Grid container spacing={isMobile ? 2 : 3}> {/* Weniger Abstand auf Mobile */}
            {/* Top Lagerorte */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6">Top Lagerorte</Typography>
                {topLocations.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topLocations} margin={{ top: 5, right: 0, left: -20, bottom: 5 }}> {/* Adjust margins */}
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#8884d8" name="Anzahl Bücher" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : ( <Typography variant="body2">Keine Daten verfügbar.</Typography> )}
              </Paper>
            </Grid>

            {/* Top Genres */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6">Top Genres</Typography>
                {topGenres.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={topGenres}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={isMobile ? 60 : 80} // Kleinerer Radius auf Mobile
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="_id"
                        label={({ _id, percent }) => `${_id} ${(percent * 100).toFixed(0)}%`} // Zeige Prozent
                      >
                        {topGenres.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : ( <Typography variant="body2">Keine Daten verfügbar.</Typography> )}
              </Paper>
            </Grid>

            {/* Anzahl Bücher gesamt */}
            <Grid item xs={12} sm={6} md={4}> {/* Angepasste Grid-Größen */}
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6">Bücher gesamt</Typography>
                <Typography variant="h4">{totalBooks}</Typography>
              </Paper>
            </Grid>

             {/* Bücher ohne Cover */}
            <Grid item xs={12} sm={6} md={4}> {/* Angepasste Grid-Größen */}
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6">Ohne Cover</Typography>
                <Typography variant="h4">{booksWithoutCover}</Typography>
              </Paper>
            </Grid>

            {/* Bücher pro Lagerort */}
            <Grid item xs={12} md={12}> {/* Volle Breite für die letzte Tabelle */}
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6">Bücher pro Lagerort</Typography>
                 {booksByLocation.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={booksByLocation} margin={{ top: 5, right: 0, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#82ca9d" name="Anzahl Bücher" />
                      </BarChart>
                    </ResponsiveContainer>
                 ) : ( <Typography variant="body2">Keine Daten verfügbar.</Typography> )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {/* Actions nur auf Desktop */}
      {!isMobile && (
          <DialogActions>
            <Button onClick={onClose}>Schließen</Button>
          </DialogActions>
      )}
    </Dialog>
  );
};

export default StatsDialog;
